<!--
@component
CommentaryCardPlaceholder

Per-card placeholder for commentary entries. Mirrors the rendered
`Default.svelte` layout: a `.commentary` block (with an optional minute
column on desktop). Used as the `LazyMount` placeholder for the
commentary feed on `MatchPage`.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';

  type Props = {
    /** Accessible label. Pass `null` to omit the live-region wrapper. */
    label?: string | null;
  };

  let { label = 'Loading commentary' }: Props = $props();

  const isMobile = $derived(appState.windowWidth <= 660);
</script>

{#snippet card()}
  <div class="placeholder commentary-card-placeholder" aria-hidden="true">
    {#if !isMobile}
      <div class="minute"></div>
    {/if}

    <div class="commentary">
      <div class="content">
        <div class="comment">
          {#if isMobile}
            <div class="minute inline"></div>
          {/if}
          <div class="line"></div>
          <div class="line short"></div>
        </div>
      </div>
    </div>
  </div>
{/snippet}

{#if label}
  <div role="status" aria-busy="true" aria-live="polite" aria-label={label}>
    {@render card()}
    <span class="visually-hidden">{label}</span>
  </div>
{:else}
  {@render card()}
{/if}

<style lang="scss">
  @use '../../../../styles/mixins' as mixins;
  @use './layout' as *;

  // Wrapper inherits Default.svelte's outer geometry so the placeholder
  // occupies the same room while not mounted.
  .placeholder.commentary-card-placeholder {
    position: relative;
    max-width: $max-width;
    margin: 2rem auto;

    @include on-mobile {
      margin: 1rem auto;
    }
  }

  // Faux minute label (desktop column or inline on mobile).
  .minute {
    position: absolute;
    top: 0;
    left: -5rem;
    width: 3rem;
    height: 0.9rem;
    border-radius: var(--theme-style-border-radius);
    background-color: var(--theme-colour-tan-grey);

    @include on-mobile {
      top: -2.8rem;
      left: 0.75rem;
    }

    &.inline {
      position: static;
      margin-bottom: 0.5rem;
    }
  }

  .commentary {
    position: relative;
    max-width: $max-width;
    border: 1px solid var(--theme-style-commentary-border-colour);
    border-radius: calc(0.5 * var(--theme-style-border-radius));
    background-color: var(--theme-colour-muted-grey);
    overflow: hidden;
    padding: ($padding * 0.5) $padding;

    @include on-mobile {
      margin: 0 ($padding * 0.25);
      padding: 0.75rem 2rem 1.5rem;
    }

    // Card-wide shimmer overlay.
    &:before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      mix-blend-mode: multiply;
      @include mixins.loading;
    }
  }

  .comment {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  // Two faux paragraph lines so the placeholder has roughly the same
  // vertical footprint as a typical commentary entry.
  .line {
    height: 0.75rem;
    border-radius: var(--theme-style-border-radius);
    background-color: var(--theme-colour-tan-grey);
    width: 100%;

    &.short {
      width: 65%;
    }
  }
</style>
